import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiResponse, PaginatedApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import { CostSourceFilter, ProcessKey } from '../components/cost-analysis/models/cost-analysis.models';

export type CostMode = 'Real' | 'Standard' | 'Mixed';

export interface PeriodSummaryReportDto {
  year: number;
  month: number;
  iteration: number;
  computedAt: string;
  rollCount: number;
  productionLbs: number;
  processedLbs: number;
  excludedCounts: ExcludedRollCountsDto;
}

export interface ExcludedRollCountsDto {
  greige: number;
  yarn: number;
  unknown: number;
}

export interface HierarchyContextDto {
  fabricCode?: string | null;
  fabricDescription?: string | null;
  colorReference?: string | null;
  colorName?: string | null;
  labDipNumber?: string | null;
  workOrderReference?: string | null;
  workOrderNumber?: string | null;
  workOrderType?: number | null;
  customerReference?: string | null;
  customerCode?: string | null;
  customerName?: string | null;
  productionCardReference?: string | null;
  productionCardNumber?: string | null;
  productionCardStatus?: number | null;
  knittingOrderReference?: string | null;
  knittingOrderNumber?: string | null;
  rollNumber?: string | null;
}

export interface ProcessCellDto {
  totalCostUsd: number;
  totalLbs: number;
  costPerLbUsd: number;
}

export interface ProcessSummaryRowDto {
  process: ProcessKey;
  label: string;
  totalLbs: number;
  totalCostUsd: number;
  costPerLbUsd: number;
  realCostPerLbUsd: number;
  standardCostPerLbUsd: number;
  variancePerLb: number;
  variancePct: number | null;
  pctOfTotal: number;
  isPending: boolean;
  locationCode?: string | null;
  subRows?: ProcessSummaryRowDto[];
}

export interface FabricProcessCellRowDto {
  fabricCode: string;
  fabricDescription: string;
  costMode: CostMode;
  totalLbs: number;
  totalCostUsd: number;
  totalCostPerLbUsd: number;
  byProcess: Record<ProcessKey, ProcessCellDto>;
  context: HierarchyContextDto;
}

export interface FabricMatrixTotalsDto {
  totalLbs: number;
  totalCostUsd: number;
  costPerLbUsd: number;
  processCostPerLb: Record<ProcessKey, number>;
  sumOfProcessCostPerLb: number;
}

export interface ProcessSummaryReportDto {
  year: number;
  month: number;
  iteration: number;
  costSource: CostSourceFilter;
  computedAt: string;
  processRows: ProcessSummaryRowDto[];
  fabricMatrix: FabricProcessCellRowDto[];
  matrixTotals: FabricMatrixTotalsDto;
}

export interface ComponentRowDto {
  component: string;
  label: string;
  realCostUsd: number;
  realPerLb: number;
  stdPerLb: number;
  variancePerLb: number;
  variancePct: number | null;
  pctOfTotal: number;
  byProcess: Record<ProcessKey, number>;
  children?: ComponentRowDto[] | null;
}

export interface ComponentTotalsDto {
  totalCostUsd: number;
  totalRealPerLb: number;
  totalStdPerLb: number;
  totalVariancePerLb: number;
  totalVariancePct: number | null;
  pctOfTotal: number;
  byProcess: Record<ProcessKey, number>;
  sumOfProcessCostPerLb: number;
}

export interface ComponentSummaryReportDto {
  year: number;
  month: number;
  iteration: number;
  costSource: CostSourceFilter;
  computedAt: string;
  rows: ComponentRowDto[];
  totals: ComponentTotalsDto;
}

export interface ProcessCostBucketDto {
  totalCostUsd: number;
  totalLbs: number;
  costPerLbUsd: number;
  realTotalCostUsd: number;
  realTotalLbs: number;
  realCostPerLbUsd: number;
  standardTotalCostUsd: number;
  standardTotalLbs: number;
  standardCostPerLbUsd: number;
}

export interface FabricTreeNodeDto {
  id: string;
  label: string;
  subLabel?: string | null;
  level: 'fabric' | 'color' | 'workOrder' | 'productionCard' | 'knittingOrder' | 'roll';
  totalLbs: number;
  totalCostUsd: number;
  costPerLbUsd: number;
  costMode: CostMode;
  byProcess: Record<ProcessKey, ProcessCostBucketDto>;
  context: HierarchyContextDto;
  children: FabricTreeNodeDto[];
}

export interface FabricSummarySearchResult extends PaginatedApiResponse<FabricTreeNodeDto> {
  year: number;
  month: number;
  costSource: CostSourceFilter;
}

export interface CostReportSearchCommand {
  costSource: CostSourceFilter;
  fabricCodes?: string[] | null;
  colorReferences?: string[] | null;
  productionCardNumbers?: string[] | null;
  workOrderReferences?: string[] | null;
  rollNumbers?: string[] | null;
  customerReferences?: string[] | null;
  /**
   * Numeric values from the OrderType enum. Sent as numbers so the API can
   * deserialize directly into its int list — the C# command lives in
   * Sierra.Finance.Models which intentionally does not reference Order.Models.
   */
  orderTypes?: number[] | null;
}

const COST_MODE_BY_WIRE: Record<number, CostMode> = {
  0: 'Real',
  1: 'Standard',
  2: 'Mixed',
};

const COST_SOURCE_BY_WIRE: Record<number, CostSourceFilter> = {
  0: 'Estimated',
  1: 'Real',
  2: 'Standard',
};

const COST_SOURCE_TO_WIRE: Record<CostSourceFilter, number> = {
  Estimated: 0,
  Real: 1,
  Standard: 2,
};

@Injectable({ providedIn: 'root' })
export class CostReportsProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/cost-reports';
  private readonly reconciliationUrl = environment.proxyUrls.financeApi + '/api/v1/cost-reconciliation';
  private readonly headers = new HttpHeaders({ 'Skip-Error-Display': 'true' });

  public getPeriodSummary(
    year: number,
    month: number,
    costSource: CostSourceFilter,
    onlyCompletedRolls = false,
  ): Observable<ApiResponse<PeriodSummaryReportDto>> {
    const params = this.withOnlyCompleted(this.costSourceParam(costSource), onlyCompletedRolls);
    return this.httpClient
      .get<ApiResponse<PeriodSummaryReportDtoWire>>(`${this.baseUrl}/period-summary/${year}/${month}`, { params, headers: this.headers })
      .pipe(map((r) => mapApiResponse(r, adaptPeriodSummary)));
  }

  public getProcessSummaryReport(
    year: number,
    month: number,
    costSource: CostSourceFilter,
  ): Observable<ApiResponse<ProcessSummaryReportDto>> {
    const params = this.costSourceParam(costSource);
    return this.httpClient
      .get<ApiResponse<ProcessSummaryReportWire>>(`${this.baseUrl}/by-process/${year}/${month}`, { params, headers: this.headers })
      .pipe(map((r) => mapApiResponse(r, adaptProcessSummary)));
  }

  public downloadProcessSummaryExcel(
    year: number,
    month: number,
    costSource: CostSourceFilter,
    onlyCompletedRolls = false,
  ): Observable<Blob> {
    const params = this.withOnlyCompleted(this.costSourceParam(costSource).set('format', 1), onlyCompletedRolls);
    return this.httpClient.get(`${this.baseUrl}/by-process/${year}/${month}`, {
      params,
      headers: this.headers,
      responseType: 'blob',
    });
  }

  public getComponentSummaryReport(
    year: number,
    month: number,
    costSource: CostSourceFilter,
  ): Observable<ApiResponse<ComponentSummaryReportDto>> {
    const params = this.costSourceParam(costSource);
    return this.httpClient
      .get<ApiResponse<ComponentSummaryReportWire>>(`${this.baseUrl}/by-component/${year}/${month}`, { params, headers: this.headers })
      .pipe(map((r) => mapApiResponse(r, adaptComponentSummary)));
  }

  public downloadComponentSummaryExcel(year: number, month: number, costSource: CostSourceFilter): Observable<Blob> {
    const params = this.costSourceParam(costSource).set('format', 1);
    return this.httpClient.get(`${this.baseUrl}/by-component/${year}/${month}`, {
      params,
      headers: this.headers,
      responseType: 'blob',
    });
  }

  public searchProcessSummaryReport(
    year: number,
    month: number,
    cmd: CostReportSearchCommand,
    onlyCompletedRolls = false,
  ): Observable<ApiResponse<ProcessSummaryReportDto>> {
    const params = this.withOnlyCompleted(new HttpParams(), onlyCompletedRolls);
    const wire = toCostReportSearchWire(cmd);
    return this.httpClient
      .post<
        ApiResponse<ProcessSummaryReportWire>
      >(`${this.baseUrl}/by-process/${year}/${month}/search`, wire, { params, headers: this.headers })
      .pipe(map((r) => mapApiResponse(r, adaptProcessSummary)));
  }

  public searchComponentSummaryReport(
    year: number,
    month: number,
    cmd: CostReportSearchCommand,
    onlyCompletedRolls = false,
  ): Observable<ApiResponse<ComponentSummaryReportDto>> {
    const params = this.withOnlyCompleted(new HttpParams(), onlyCompletedRolls);
    const wire = toCostReportSearchWire(cmd);
    return this.httpClient
      .post<
        ApiResponse<ComponentSummaryReportWire>
      >(`${this.baseUrl}/by-component/${year}/${month}/search`, wire, { params, headers: this.headers })
      .pipe(map((r) => mapApiResponse(r, adaptComponentSummary)));
  }

  public searchFabricSummary(
    year: number,
    month: number,
    cmd: CostReportSearchCommand,
    pageNumber: number,
    pageSize: number,
    onlyCompletedRolls = false,
  ): Observable<ApiResponse<FabricSummarySearchResult>> {
    const params = this.withOnlyCompleted(new HttpParams().set('pageNumber', pageNumber).set('pageSize', pageSize), onlyCompletedRolls);
    const wire = toCostReportSearchWire(cmd);
    return this.httpClient
      .post<
        ApiResponse<FabricSummarySearchResultWire>
      >(`${this.baseUrl}/by-fabric/${year}/${month}/search`, wire, { params, headers: this.headers })
      .pipe(map((r) => mapApiResponse(r, adaptFabricSummarySearch)));
  }

  public expandFabric(
    year: number,
    month: number,
    fabricCode: string,
    cmd: CostReportSearchCommand,
    onlyCompletedRolls = false,
  ): Observable<ApiResponse<FabricTreeNodeDto[]>> {
    const params = this.withOnlyCompleted(new HttpParams(), onlyCompletedRolls);
    const wire = toCostReportSearchWire(cmd);
    return this.httpClient
      .post<
        ApiResponse<FabricTreeNodeWire[]>
      >(`${this.baseUrl}/by-fabric/${year}/${month}/${encodeURIComponent(fabricCode)}/expand`, wire, { params, headers: this.headers })
      .pipe(map((r) => mapApiResponse(r, (arr) => arr.map(adaptFabricTreeNode))));
  }

  public downloadFabricSummaryExcel(
    year: number,
    month: number,
    cmd: CostReportSearchCommand,
    onlyCompletedRolls = false,
  ): Observable<Blob> {
    const params = this.withOnlyCompleted(new HttpParams().set('format', 1), onlyCompletedRolls);
    const wire = toCostReportSearchWire(cmd);
    return this.httpClient.post(`${this.baseUrl}/by-fabric/${year}/${month}/search`, wire, {
      params,
      headers: this.headers,
      responseType: 'blob',
    });
  }

  public downloadDailyProductionExcel(fromDate: Date, toDate: Date, offsetMinutes: number, processes: string[]): Observable<Blob> {
    let params = new HttpParams()
      .set('fromDate', fromDate.toISOString())
      .set('toDate', toDate.toISOString())
      .set('offsetMinutes', offsetMinutes)
      .set('format', 1);
    processes.forEach((process) => (params = params.append('processes', process)));
    return this.httpClient.get(`${this.baseUrl}/daily-production`, {
      params,
      headers: this.headers,
      responseType: 'blob',
    });
  }

  public downloadReconciliationExcel(costKey: string, year: number, month: number): Observable<Blob> {
    return this.httpClient.get(`${this.reconciliationUrl}/${costKey}/${year}/${month}`, {
      headers: this.headers,
      responseType: 'blob',
    });
  }

  private costSourceParam(costSource: CostSourceFilter): HttpParams {
    return new HttpParams().set('costSource', COST_SOURCE_TO_WIRE[costSource]);
  }

  private withOnlyCompleted(params: HttpParams, onlyCompletedRolls: boolean): HttpParams {
    return onlyCompletedRolls ? params.set('onlyCompletedRolls', true) : params;
  }
}

type PeriodSummaryReportDtoWire = PeriodSummaryReportDto;

interface ProcessSummaryReportWire {
  year: number;
  month: number;
  iteration: number;
  costSource: number;
  computedAt: string;
  processRows: ProcessSummaryRowWire[];
  fabricMatrix: FabricByProcessRowWire[];
  matrixTotals: FabricMatrixTotalsWire;
}

interface ProcessSummaryRowWire extends Omit<ProcessSummaryRowDto, 'process'> {
  process: string;
}

interface FabricByProcessRowWire {
  fabricCode: string;
  fabricDescription: string;
  costMode: number;
  totalLbs: number;
  totalCostUsd: number;
  totalCostPerLbUsd: number;
  byProcess: Record<string, ProcessCellDto>;
  context: HierarchyContextDto;
}

interface FabricMatrixTotalsWire {
  totalLbs: number;
  totalCostUsd: number;
  costPerLbUsd: number;
  processCostPerLb: Record<string, number>;
  sumOfProcessCostPerLb: number;
}

interface ComponentSummaryReportWire {
  year: number;
  month: number;
  iteration: number;
  costSource: number;
  computedAt: string;
  rows: ComponentRowWire[];
  totals: ComponentTotalsWire;
}

interface ComponentRowWire {
  component: string;
  label: string;
  realCostUsd: number;
  realPerLb: number;
  stdPerLb: number;
  variancePerLb: number;
  variancePct: number | null;
  pctOfTotal: number;
  byProcess: Record<string, number>;
  children?: ComponentRowWire[] | null;
}

interface ComponentTotalsWire {
  totalCostUsd: number;
  totalRealPerLb: number;
  totalStdPerLb: number;
  totalVariancePerLb: number;
  totalVariancePct: number | null;
  pctOfTotal: number;
  byProcess: Record<string, number>;
  sumOfProcessCostPerLb: number;
}

interface FabricSummarySearchResultWire extends PaginatedApiResponse<FabricTreeNodeWire> {
  year: number;
  month: number;
  costSource: number;
}

interface FabricTreeNodeWire {
  id: string;
  label: string;
  subLabel?: string | null;
  level: 'fabric' | 'color' | 'workOrder' | 'productionCard' | 'knittingOrder' | 'roll';
  totalLbs: number;
  totalCostUsd: number;
  costPerLbUsd: number;
  costMode: number;
  byProcess: Record<string, ProcessCostBucketDto>;
  context: HierarchyContextDto;
  children: FabricTreeNodeWire[];
}

interface CostReportSearchCommandWire {
  costSource: number;
  fabricCodes?: string[] | null;
  colorReferences?: string[] | null;
  productionCardNumbers?: string[] | null;
  workOrderReferences?: string[] | null;
  rollNumbers?: string[] | null;
  customerReferences?: string[] | null;
  orderTypes?: number[] | null;
}

function mapApiResponse<TWire, TPublic>(response: ApiResponse<TWire>, adapter: (w: TWire) => TPublic): ApiResponse<TPublic> {
  return {
    ...response,
    data: response.data ? adapter(response.data) : null,
  };
}

function adaptPeriodSummary(w: PeriodSummaryReportDtoWire): PeriodSummaryReportDto {
  return w;
}

function adaptProcessSummary(w: ProcessSummaryReportWire): ProcessSummaryReportDto {
  return {
    year: w.year,
    month: w.month,
    iteration: w.iteration,
    costSource: COST_SOURCE_BY_WIRE[w.costSource],
    computedAt: w.computedAt,
    processRows: w.processRows.map((r) => ({
      ...r,
      process: r.process as ProcessKey,
    })),
    fabricMatrix: w.fabricMatrix.map((r) => ({
      fabricCode: r.fabricCode,
      fabricDescription: r.fabricDescription,
      costMode: COST_MODE_BY_WIRE[r.costMode],
      totalLbs: r.totalLbs,
      totalCostUsd: r.totalCostUsd,
      totalCostPerLbUsd: r.totalCostPerLbUsd,
      byProcess: r.byProcess as Record<ProcessKey, ProcessCellDto>,
      context: r.context,
    })),
    matrixTotals: {
      totalLbs: w.matrixTotals.totalLbs,
      totalCostUsd: w.matrixTotals.totalCostUsd,
      costPerLbUsd: w.matrixTotals.costPerLbUsd,
      processCostPerLb: w.matrixTotals.processCostPerLb as Record<ProcessKey, number>,
      sumOfProcessCostPerLb: w.matrixTotals.sumOfProcessCostPerLb,
    },
  };
}

function adaptComponentSummary(w: ComponentSummaryReportWire): ComponentSummaryReportDto {
  return {
    year: w.year,
    month: w.month,
    iteration: w.iteration,
    costSource: COST_SOURCE_BY_WIRE[w.costSource],
    computedAt: w.computedAt,
    rows: w.rows.map(adaptComponentRow),
    totals: {
      totalCostUsd: w.totals.totalCostUsd,
      totalRealPerLb: w.totals.totalRealPerLb,
      totalStdPerLb: w.totals.totalStdPerLb,
      totalVariancePerLb: w.totals.totalVariancePerLb,
      totalVariancePct: w.totals.totalVariancePct,
      pctOfTotal: w.totals.pctOfTotal,
      byProcess: w.totals.byProcess as Record<ProcessKey, number>,
      sumOfProcessCostPerLb: w.totals.sumOfProcessCostPerLb,
    },
  };
}

function adaptComponentRow(w: ComponentRowWire): ComponentRowDto {
  return {
    component: w.component,
    label: w.label,
    realCostUsd: w.realCostUsd,
    realPerLb: w.realPerLb,
    stdPerLb: w.stdPerLb,
    variancePerLb: w.variancePerLb,
    variancePct: w.variancePct,
    pctOfTotal: w.pctOfTotal,
    byProcess: w.byProcess as Record<ProcessKey, number>,
    children: w.children ? w.children.map(adaptComponentRow) : null,
  };
}

function adaptFabricSummarySearch(w: FabricSummarySearchResultWire): FabricSummarySearchResult {
  return {
    year: w.year,
    month: w.month,
    costSource: COST_SOURCE_BY_WIRE[w.costSource],
    totalCount: w.totalCount,
    pageNumber: w.pageNumber,
    pageSize: w.pageSize,
    items: w.items.map(adaptFabricTreeNode),
  };
}

function adaptFabricTreeNode(n: FabricTreeNodeWire): FabricTreeNodeDto {
  return {
    id: n.id,
    label: n.label,
    subLabel: n.subLabel,
    level: n.level,
    totalLbs: n.totalLbs,
    totalCostUsd: n.totalCostUsd,
    costPerLbUsd: n.costPerLbUsd,
    costMode: COST_MODE_BY_WIRE[n.costMode],
    byProcess: n.byProcess as Record<ProcessKey, ProcessCostBucketDto>,
    context: n.context,
    children: n.children.map(adaptFabricTreeNode),
  };
}

function toCostReportSearchWire(cmd: CostReportSearchCommand): CostReportSearchCommandWire {
  return {
    costSource: COST_SOURCE_TO_WIRE[cmd.costSource],
    fabricCodes: cmd.fabricCodes,
    colorReferences: cmd.colorReferences,
    productionCardNumbers: cmd.productionCardNumbers,
    workOrderReferences: cmd.workOrderReferences,
    rollNumbers: cmd.rollNumbers,
    customerReferences: cmd.customerReferences,
    orderTypes: cmd.orderTypes,
  };
}
